import {
    SiSpotify,
    SiTidal,
    SiBandcamp,
    SiDeezer,
    SiApple,
    SiSoundcloud,
} from '@icons-pack/react-simple-icons';
import { Head } from '@inertiajs/react';
import {ShoppingCart, Music} from 'lucide-react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import ResponsiveImage from '@/components/responsive-image';
import Layout from '@/layouts/layout';
import { toUrl, cn} from '@/lib/utils';

export default function Album({album}) {
    const {
        title,
        release_year,
        artists,
        description,
        label,
        cover,
        offers,
        links
    } = album.data;

    return (
        <Layout>
            <Head title={title} />
            <AlbumWrapper>
                <AlbumImage>
                    {cover != null && (
                        <ResponsiveImage
                            fetchPriority='high'
                            className="object-contain rounded-sm border shadow-lg"
                            srcSet={cover.srcset}
                            height={cover.width}
                            width={cover.height}
                            alt={`${title} Album Cover`}
                        />
                    )}
                </AlbumImage>
                <AlbumInfo>
                    <div className="text-2xl flex space-x-1 max-sm:flex-col">
                        <span className='font-bold'>{title}</span>
                        <span>({release_year})</span>
                    </div>

                    {label && (
                        <a
                            href={label.url ? toUrl(label.url) : '#'}
                            className='text-lg italic hover:bg-primary hover:text-secondary max-sm:underline'
                            target='_blank'
                            rel='noopener noreferrer'
                        >{label.name}</a>
                    )}
                    <AlbumOffers offers={offers} />
                </AlbumInfo>
                <AlbumCredits artists={artists} />
                <AlbumDescription>
                    {description && (
                        <Markdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                        >{description}</Markdown>)}

                    {links && links.length > 0 && (
                        <div className='flex flex-col'>
                            <span className="text-2xl  text-primary pb-2">Reviews</span>
                            {links
                            .filter(link => link.type === 'review')
                            .map((link, idx) => (
                                <div key={idx}>
                                    <a
                                        href={link.url}
                                        className='text-lg italic hover:bg-primary hover:text-secondary'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >[{new URL(link.url).hostname}]</a>
                                </div>
                            ))}
                        </div>
                    )}
                </AlbumDescription>
            </AlbumWrapper>
        </Layout>
    );
}

const AlbumWrapper = ({children}) => {
    return (
        <div className='grid grid-cols-1 lg:grid-cols-3 p-4 gap-4 lg:gap-12 pt-10 lg:pt-50'>
        {children}
        </div>
    )
}

const AlbumImage = ({children}) => {
    return (
        <div className='flex items-center animate-in-out duration-150 w-full'>
            {children}
        </div>
    )
}

const AlbumDescription = ({children}) => {
    return (
        <div className="prose lg:prose-xl prose-zinc dark:prose-invert lg:col-span-2">
            {children}
        </div>
    )
}

const AlbumInfo = ({children}) => {
    return (
        <div className="space-y-1 lg:col-span-2">
            {children}
        </div>
    )
}

const AlbumCredits = ({artists}) => {
    const band = artists.filter(artist => artist.pivot.role === 'band');
    const guests = artists.filter(artist => artist.pivot.role === 'guests');
    const crew = artists.filter(artist => artist.pivot.role === 'crew');
    return (
        <div className="flex flex-col items-center space-y-20">
            {band && (
                <div className='w-full'>
                    <div className='space-y-2'>
                        {band.map((artist, idx) => (
                            <AlbumArtist key={idx} {...artist} />
                        ))}
                    </div>
                </div>
            )}
            {guests && guests.length > 0 && (
                <div className='w-full'>
                    <span className='text-muted text-xl'>Guests</span>
                    <div className='space-y-2 pt-4'>
                        {guests.map((artist, idx) => (
                            <AlbumArtist key={idx} {...artist} />
                        ))}
                    </div>
                </div>
            )}
            {crew && crew.length > 0 && (
                <div className='w-full'>
                    <span className='text-muted text-xl'>Production</span>
                    <div className='space-y-2 pt-4'>
                        {crew.map((artist, idx) => (
                            <AlbumArtist key={idx} {...artist} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

const AlbumArtist = ({name, pivot}) => {
    return (
        <div className='flex items-center justify-center '>
            <span className="text-lg min-w-1/2 flex-1 self-start font-medium">{name}</span>
            <span className="text-muted flex-1">{pivot.description}</span>
        </div>
    )
}

const AlbumOffers = ({offers}) => {
    // @todo group backend
    const buy = offers.filter(offer => offer.type === 'buy');
    const listen = offers.filter(offer => offer.type === 'listen');

    // @todo DRY
    return (
        <div className='grid grid-cols-1  gap-4 pt-4'>
            {buy.length > 0 && (
                <OfferWrapper className=''>
                    <OfferHeader>
                        <ShoppingCart className='text-muted' />
                        <span>Buy</span>
                    </OfferHeader>
                    <OfferContent>
                        {buy.map((offer) => (
                            <Offer key={offer.remote.slug} {...offer} />
                        ))}
                    </OfferContent>
                </OfferWrapper>
            )}

            {listen.length > 0 && (
                <OfferWrapper>
                    <OfferHeader>
                        <Music className='text-muted' />
                        <span>Listen</span>
                    </OfferHeader>
                    <OfferContent>
                        {listen.map((offer) => (
                            <Offer key={offer.remote.slug} {...offer} />
                        ))}
                    </OfferContent>
                </OfferWrapper>
            )}
        </div>
    )
}
const OfferContent = ({children}) => {
    return (
        <div className="grid gap-2 lg:grid-cols-3">
            {children}
        </div>
    )
}
const OfferHeader = ({children}) => {
    return (
        <div className="flex items-center gap-4 p-4 text-lg font-medium lg:px-0">
            {children}
        </div>
    )
}

const OfferWrapper = ({children, className}) => {
    return (
        <div className={cn("justify-between pb-4", className)}>
            {children}
        </div>
    )
}

const Offer = ({remote, url}) => {

    let Icon = null;
    switch(remote.slug) {
        case 'bandcamp':
            Icon = SiBandcamp;
            break;
        case 'tidal':
            Icon = SiTidal
            break;
        case 'spotify':
            Icon = SiSpotify
            break;
        case 'apple-music':
            Icon = SiApple
            break;
        case 'deezer':
            Icon = SiDeezer
            break;
        case 'soundcloud':
            Icon = SiSoundcloud
            break;
    }

    return (
        <a
            href={toUrl(url)}
            target='_blank'
            rel='noopener noreferrer'
            className='group flex items-center gap-2 hover:bg-primary hover:text-secondary p-4 bg-accent rounded-sm lg:min-w-1/4 lg:p-3 lg:text-sm'
        >
            {Icon && (<Icon className='size-5 opacity-80 group-hover:opacity-100' />)}
            <span>{remote.name}</span>
        </a>
    )
}
